<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50 p-4"
    @click.self="$emit('close')"
  >
    <div class="w-full max-w-md">
      <div class="bg-white rounded-lg shadow-xl p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-gray-900">
            <template v-if="userName">Link Entity to {{ userName }}</template>
            <template v-else>Link Entity</template>
          </h3>
          <button @click="$emit('close')" class="text-gray-400 hover:text-gray-500">
            <FontAwesomeIcon :icon="['fas', 'times']" />
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
            <select
              v-model="selectedTypeId"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
            >
              <option value="">Choose an entity type...</option>
              <option v-for="et in entityTypes" :key="et.id" :value="et.id">
                {{ et.type_name }}
              </option>
            </select>
          </div>

          <div v-if="selectedTypeId">
            <label class="block text-sm font-medium text-gray-700 mb-1">Entity</label>
            <input
              v-model="entitySearch"
              :placeholder="loadingEntities ? 'Loading...' : 'Filter by name...'"
              :disabled="loadingEntities"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4 disabled:opacity-50 mb-2"
            />
            <div v-if="loadingEntities" class="space-y-2">
              <div v-for="n in 4" :key="n" class="h-8 bg-gray-100 rounded animate-pulse"></div>
            </div>
            <div v-else-if="filteredEntities.length === 0" class="text-sm text-gray-400 italic py-3 text-center">
              No unlinked entities found
            </div>
            <div v-else class="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
              <button
                v-for="entity in filteredEntities"
                :key="entity.id"
                class="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                :class="{ 'bg-blue-100 font-medium': selectedEntity && selectedEntity.id === entity.id }"
                @click="selectEntity(entity)"
              >
                {{ entity.name }}
              </button>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="$emit('close')"
            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="submit"
            :disabled="!selectedTypeId || !selectedEntity"
            class="btn-primary-sm disabled:opacity-50"
          >
            <FontAwesomeIcon :icon="['fas', 'link']" class="mr-1" />
            Link Entity
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { getUserAssignableTypes, getUnlinkedEntities } from '@/services/api/entities';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTimes, faLink } from '@fortawesome/free-solid-svg-icons';

library.add(faTimes, faLink);

export default {
  name: 'LinkEntityModal',

  components: { FontAwesomeIcon },

  props: {
    isVisible: { type: Boolean, default: false },
    userName: { type: String, default: '' },
  },

  emits: ['close', 'link'],

  data() {
    return {
      entityTypes: [],
      selectedTypeId: '',
      entities: [],
      loadingEntities: false,
      entitySearch: '',
      selectedEntity: null,
    };
  },

  computed: {
    filteredEntities() {
      if (!this.entitySearch) return this.entities;
      const q = this.entitySearch.toLowerCase();
      return this.entities.filter(e => e.name.toLowerCase().includes(q));
    },
  },

  watch: {
    async selectedTypeId(typeId) {
      this.entitySearch = '';
      this.selectedEntity = null;
      this.entities = [];
      if (!typeId) return;

      this.loadingEntities = true;
      try {
        this.entities = await getUnlinkedEntities(typeId);
        this.entities.sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        console.error('Failed to load entities:', error);
        this.entities = [];
      } finally {
        this.loadingEntities = false;
      }
    },

    isVisible(visible) {
      if (visible && this.entityTypes.length === 0) {
        this.fetchEntityTypes();
      }
      if (!visible) {
        this.selectedTypeId = '';
        this.entitySearch = '';
        this.selectedEntity = null;
        this.entities = [];
      }
    },
  },

  methods: {
    async fetchEntityTypes() {
      try {
        this.entityTypes = await getUserAssignableTypes();
      } catch (error) {
        console.error('Failed to load entity types:', error);
      }
    },

    selectEntity(entity) {
      this.selectedEntity = entity;
    },

    submit() {
      if (!this.selectedTypeId || !this.selectedEntity) return;
      this.$emit('link', {
        entity_type_id: this.selectedTypeId,
        existing_entity_id: this.selectedEntity.id,
      });
    },
  },
};
</script>
